import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

/**
 * Preferencia de tema del frontend: claro / oscuro / automático.
 *
 * - Se persiste en el navegador (localStorage), no en el backend.
 * - 'auto' sigue la preferencia del sistema (prefers-color-scheme) y reacciona
 *   si cambia en caliente.
 * - `resolved` es el tema efectivo ('light' | 'dark') que consume el ConfigProvider.
 */
const STORAGE_KEY = 'theme-mode';
const VALID = ['light', 'dark', 'auto'];

const ThemeModeContext = createContext({ mode: 'auto', resolved: 'light', setMode: () => {} });

export const useThemeMode = () => useContext(ThemeModeContext);

const readStoredMode = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(s) ? s : 'auto';
  } catch (e) {
    return 'auto';
  }
};

const systemPrefersDark = () => {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch (e) {
    return false;
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Reaccionar a cambios del tema del sistema (relevante en modo 'auto').
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const setMode = useCallback((m) => {
    const next = VALID.includes(m) ? m : 'auto';
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* localStorage no disponible: la preferencia vive solo en memoria */
    }
  }, []);

  const resolved = mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode;

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};
